import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Package, ShoppingCart, Save, Globe, Layout, Eye, Tag, Megaphone, Mail, FileText, Construction, AlertTriangle 
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { toast } from 'sonner';
import ImageUploader from '../ImageUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContentTab = () => {
  const [siteContent, setSiteContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(true);
  const [savingContent, setSavingContent] = useState(false);
  const [contentTab, setContentTab] = useState('site');
  const [siteStatus, setSiteStatus] = useState({ under_construction: false, construction_message: '', redirect_url: '' });
  const [savingSiteStatus, setSavingSiteStatus] = useState(false);

  const fetchContent = useCallback(async () => {
    try {
      setContentLoading(true);
      const response = await axios.get(`${API}/admin/content`);
      setSiteContent(response.data);
      if (response.data.site_status) {
        setSiteStatus(response.data.site_status);
      }
    } catch (error) {
      toast.error('Failed to load content');
    } finally {
      setContentLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const getContent = (path, defaultValue = '') => {
    if (!siteContent) return defaultValue;
    const keys = path.split('.');
    let value = siteContent;
    for (const key of keys) {
      value = value?.[key];
      if (value === undefined) return defaultValue;
    }
    return value;
  };

  const updateContent = (path, value) => {
    const keys = path.split('.');
    setSiteContent(prev => {
      const newContent = JSON.parse(JSON.stringify(prev));
      let current = newContent;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newContent;
    });
  };

  const handleSaveContent = async () => {
    try {
      setSavingContent(true);
      await axios.put(`${API}/admin/content`, { content: siteContent });
      toast.success('Content saved successfully');
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSavingContent(false);
    }
  };

  const saveSiteStatus = async () => {
    try {
      setSavingSiteStatus(true);
      await axios.put(`${API}/admin/site-status`, siteStatus);
      toast.success(siteStatus.under_construction ? 'Construction mode enabled' : 'Site is now LIVE');
    } catch (error) {
      toast.error('Failed to update site status');
    } finally {
      setSavingSiteStatus(false);
    }
  };

  if (contentLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-32 bg-sand rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (!siteContent) {
    return (
      <div className="text-center py-16 bg-white rounded-2xl">
        <FileText className="w-12 h-12 text-text-muted mx-auto mb-4" />
        <p className="text-text-secondary">Failed to load content</p>
        <Button onClick={fetchContent} className="mt-4 rounded-full">Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="content-tab">
      {/* Construction Mode Status Banner - Always visible at top */}
      <div className={`rounded-2xl p-6 ${siteStatus.under_construction ? 'bg-amber-50 border-2 border-amber-400' : 'bg-green-50 border-2 border-green-400'}`}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${siteStatus.under_construction ? 'bg-amber-400' : 'bg-green-400'}`}>
              {siteStatus.under_construction ? (
                <Construction className="w-6 h-6 text-white" />
              ) : (
                <Globe className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <h2 className={`text-xl font-bold ${siteStatus.under_construction ? 'text-amber-800' : 'text-green-800'}`}>
                Construction Mode: {siteStatus.under_construction ? 'ON' : 'OFF'}
              </h2>
              <p className={`text-sm ${siteStatus.under_construction ? 'text-amber-600' : 'text-green-600'}`}>
                {siteStatus.under_construction 
                  ? 'Visitors see a maintenance page. Only admins can access the site.' 
                  : 'Your website is live and accessible to all visitors.'}
              </p>
            </div>
          </div>
          <Button 
            onClick={() => {
              const newStatus = {...siteStatus, under_construction: !siteStatus.under_construction};
              setSiteStatus(newStatus);
              // Auto-save when toggling
              setSavingSiteStatus(true);
              axios.put(`${API}/admin/site-status`, newStatus)
                .then(() => {
                  toast.success(newStatus.under_construction ? 'Construction mode ENABLED - Site is now hidden' : 'Construction mode DISABLED - Site is now LIVE');
                })
                .catch(() => toast.error('Failed to update site status'))
                .finally(() => setSavingSiteStatus(false));
            }} 
            disabled={savingSiteStatus}
            className={`rounded-full px-6 py-3 font-bold ${siteStatus.under_construction 
              ? 'bg-green-600 hover:bg-green-700 text-white' 
              : 'bg-amber-500 hover:bg-amber-600 text-white'}`}
          >
            {savingSiteStatus ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : siteStatus.under_construction ? (
              <Globe className="w-4 h-4 mr-2" />
            ) : (
              <Construction className="w-4 h-4 mr-2" />
            )}
            {siteStatus.under_construction ? 'Set Site to LIVE' : 'Enable Construction Mode'}
          </Button>
        </div>
        
        {/* Construction Mode Settings - Only show when enabled */}
        {siteStatus.under_construction && (
          <div className="mt-4 pt-4 border-t border-amber-300">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-amber-800">Custom Message (shown to visitors)</Label>
                <Input 
                  value={siteStatus.construction_message || ''} 
                  onChange={(e) => setSiteStatus({...siteStatus, construction_message: e.target.value})} 
                  placeholder="We are currently making updates and improvements."
                  className="rounded-xl mt-1 border-amber-300" 
                />
              </div>
              <div>
                <Label className="text-amber-800">Redirect URL (optional button link)</Label>
                <Input 
                  value={siteStatus.redirect_url || ''} 
                  onChange={(e) => setSiteStatus({...siteStatus, redirect_url: e.target.value})} 
                  placeholder="https://example.com"
                  className="rounded-xl mt-1 border-amber-300" 
                />
              </div>
            </div>
            <Button 
              onClick={saveSiteStatus} 
              disabled={savingSiteStatus}
              variant="outline"
              className="mt-3 rounded-full border-amber-400 text-amber-700 hover:bg-amber-100"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Message Settings
            </Button>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-between items-center">
        <p className="text-text-muted text-sm">Edit any text or image on your website below</p>
        <Button onClick={handleSaveContent} disabled={savingContent} className="rounded-full bg-forest hover:bg-forest-dark text-white px-8">
          {savingContent ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save All Changes
        </Button>
      </div>

      {/* Content Tabs */}
      <Tabs value={contentTab} onValueChange={setContentTab} className="w-full">
        <TabsList className="bg-sand rounded-full p-1 mb-6 flex-wrap">
          <TabsTrigger value="site" className="rounded-full data-[state=active]:bg-white"><Globe className="w-4 h-4 mr-2" />Site Info</TabsTrigger>
          <TabsTrigger value="checkout" className="rounded-full data-[state=active]:bg-white"><ShoppingCart className="w-4 h-4 mr-2" />Checkout</TabsTrigger>
          <TabsTrigger value="bundles" className="rounded-full data-[state=active]:bg-white"><Package className="w-4 h-4 mr-2" />Bundles</TabsTrigger>
          <TabsTrigger value="hero" className="rounded-full data-[state=active]:bg-white"><Layout className="w-4 h-4 mr-2" />Hero</TabsTrigger>
          <TabsTrigger value="homepage" className="rounded-full data-[state=active]:bg-white"><Eye className="w-4 h-4 mr-2" />Homepage</TabsTrigger>
          <TabsTrigger value="banners" className="rounded-full data-[state=active]:bg-white"><Megaphone className="w-4 h-4 mr-2" />Banners</TabsTrigger>
          <TabsTrigger value="footer" className="rounded-full data-[state=active]:bg-white"><Mail className="w-4 h-4 mr-2" />Footer</TabsTrigger>
          <TabsTrigger value="policies" className="rounded-full data-[state=active]:bg-white"><FileText className="w-4 h-4 mr-2" />Policies</TabsTrigger>
        </TabsList>

        {/* Site Info */}
        <TabsContent value="site">
          <div className="bg-white rounded-2xl shadow-card p-6 space-y-4">
            <h3 className="font-heading text-xl text-text-primary mb-4">Site Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Site Name</Label>
                <Input value={getContent('site_info.site_name')} onChange={(e) => updateContent('site_info.site_name', e.target.value)} className="rounded-xl mt-1" />
              </div>
              <div>
                <Label>Tagline</Label>
                <Input value={getContent('site_info.tagline')} onChange={(e) => updateContent('site_info.tagline', e.target.value)} className="rounded-xl mt-1" />
              </div>
              <div>
                <ImageUploader label="Logo" currentImage={getContent('site_info.logo_url')} onImageChange={(url) => updateContent('site_info.logo_url', url)} />
              </div>
              <div>
                <ImageUploader label="Favicon" currentImage={getContent('site_info.favicon_url')} onImageChange={(url) => updateContent('site_info.favicon_url', url)} />
              </div>
            </div>
            
            <div className="border-t pt-4 mt-6">
              <h4 className="font-medium text-text-primary mb-4">Announcement Bar</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Announcement Text</Label>
                  <Input value={getContent('homepage.announcement_bar.text')} onChange={(e) => updateContent('homepage.announcement_bar.text', e.target.value)} className="rounded-xl mt-1" />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={getContent('homepage.announcement_bar.enabled', false)} onChange={(e) => updateContent('homepage.announcement_bar.enabled', e.target.checked)} className="w-5 h-5 rounded" />
                  <Label>Show Announcement Bar</Label>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Checkout Settings */}
        <TabsContent value="checkout">
          <div className="bg-white rounded-2xl shadow-card p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading text-xl text-text-primary">Checkout Settings</h3>
                <p className="text-text-muted text-sm">Configure taxes, shipping rates, and checkout options</p>
              </div>
              <Button 
                onClick={handleSaveContent} 
                disabled={savingContent}
                className="rounded-full bg-forest hover:bg-forest-dark text-white"
                data-testid="save-checkout-settings"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingContent ? 'Saving...' : 'Save Checkout Settings'}
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Label>Tax Rate (%)</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={(getContent('checkout.tax_rate', 0.08) * 100).toFixed(2)} 
                  onChange={(e) => updateContent('checkout.tax_rate', parseFloat(e.target.value) / 100 || 0)} 
                  className="rounded-xl mt-1" 
                  placeholder="8.00"
                />
                <p className="text-xs text-text-muted mt-1">Enter as percentage (e.g., 8 for 8%)</p>
              </div>
              <div>
                <Label>Shipping Rate ($)</Label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={getContent('checkout.shipping_rate', 9.99)} 
                  onChange={(e) => updateContent('checkout.shipping_rate', parseFloat(e.target.value) || 0)} 
                  className="rounded-xl mt-1" 
                  placeholder="9.99"
                />
              </div>
              <div>
                <Label>Free Shipping Threshold ($)</Label>
                <Input 
                  type="number" 
                  step="1"
                  value={getContent('checkout.free_shipping_threshold', 75)} 
                  onChange={(e) => updateContent('checkout.free_shipping_threshold', parseFloat(e.target.value) || 0)} 
                  className="rounded-xl mt-1" 
                  placeholder="75"
                />
                <p className="text-xs text-text-muted mt-1">Orders above this get free shipping</p>
              </div>
            </div>

            <div className="border-t pt-6">
              <Label>Terms & Conditions Text</Label>
              <Textarea 
                value={getContent('checkout.terms_text', '')} 
                onChange={(e) => updateContent('checkout.terms_text', e.target.value)} 
                className="rounded-xl mt-1" 
                rows={3}
                placeholder="I agree to the Terms of Use and Privacy Policy..."
              />
            </div>
          </div>
        </TabsContent>

        {/* Bundle Configuration */}
        <TabsContent value="bundles">
          <div className="bg-white rounded-2xl shadow-card p-6 space-y-6">
            <h3 className="font-heading text-xl text-text-primary">Bundle Discounts</h3>
            <p className="text-text-muted text-sm">Configure bundle pricing for each category</p>
            
            {/* Standard bundles - Teas, Supplements, Soap */}
            {['teas', 'supplements', 'soap'].map((category) => (
              <div key={category} className="border rounded-xl p-4">
                <h4 className="font-medium text-text-primary capitalize mb-3">{category}</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Bundle Text</Label>
                    <Input 
                      value={getContent(`banners.${category}.text`, '')} 
                      onChange={(e) => updateContent(`banners.${category}.text`, e.target.value)} 
                      className="rounded-xl mt-1" 
                      placeholder={`Get any ${category === 'teas' ? '3' : '2'} for $X`}
                    />
                  </div>
                  <div>
                    <Label>Savings Text</Label>
                    <Input 
                      value={getContent(`banners.${category}.savings`, '')} 
                      onChange={(e) => updateContent(`banners.${category}.savings`, e.target.value)} 
                      className="rounded-xl mt-1" 
                      placeholder="Save $X.XX"
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <input 
                      type="checkbox" 
                      checked={getContent(`banners.${category}.enabled`, true)} 
                      onChange={(e) => updateContent(`banners.${category}.enabled`, e.target.checked)} 
                      className="w-5 h-5 rounded" 
                    />
                    <Label>Show Bundle Banner</Label>
                  </div>
                </div>
              </div>
            ))}

            {/* Tiered Bundle - Supplies */}
            <div className="border-2 border-terracotta/30 rounded-xl p-4 bg-terracotta/5">
              <h4 className="font-medium text-text-primary mb-1">Supplies (Tiered Pricing)</h4>
              <p className="text-text-muted text-xs mb-4">Configure &quot;BUY MORE &amp; SAVE&quot; tier pricing for pens</p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <Label>1 Item Price ($)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={getContent('banners.supplies.tier1_price', 20)} 
                    onChange={(e) => updateContent('banners.supplies.tier1_price', parseFloat(e.target.value) || 20)} 
                    className="rounded-xl mt-1" 
                    placeholder="20"
                  />
                </div>
                <div>
                  <Label>3 Items Price ($)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={getContent('banners.supplies.tier3_price', 55)} 
                    onChange={(e) => updateContent('banners.supplies.tier3_price', parseFloat(e.target.value) || 55)} 
                    className="rounded-xl mt-1" 
                    placeholder="55"
                  />
                  <p className="text-xs text-forest mt-1">
                    Saves ${((getContent('banners.supplies.tier1_price', 20) * 3) - getContent('banners.supplies.tier3_price', 55)).toFixed(2)}
                  </p>
                </div>
                <div>
                  <Label>5 Items Price ($)</Label>
                  <Input 
                    type="number"
                    step="0.01"
                    value={getContent('banners.supplies.tier5_price', 90)} 
                    onChange={(e) => updateContent('banners.supplies.tier5_price', parseFloat(e.target.value) || 90)} 
                    className="rounded-xl mt-1" 
                    placeholder="90"
                  />
                  <p className="text-xs text-forest mt-1">
                    Saves ${((getContent('banners.supplies.tier1_price', 20) * 5) - getContent('banners.supplies.tier5_price', 90)).toFixed(2)}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Banner Text (optional)</Label>
                  <Input 
                    value={getContent('banners.supplies.text', '')} 
                    onChange={(e) => updateContent('banners.supplies.text', e.target.value)} 
                    className="rounded-xl mt-1" 
                    placeholder="Get 3 pens for $55, 5 for $90"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input 
                    type="checkbox" 
                    checked={getContent('banners.supplies.enabled', true)} 
                    onChange={(e) => updateContent('banners.supplies.enabled', e.target.checked)} 
                    className="w-5 h-5 rounded" 
                  />
                  <Label>Show Tiered Pricing Banner</Label>
                </div>
              </div>

              {/* Preview */}
              <div className="mt-4 p-3 bg-forest text-white rounded-xl">
                <p className="text-xs text-white/70 mb-2">Preview:</p>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-semibold">BUY MORE & SAVE</span>
                  <span className="bg-white/10 rounded-full px-3 py-1">1 for ${getContent('banners.supplies.tier1_price', 20)}</span>
                  <span className="bg-white/20 rounded-full px-3 py-1">3 for ${getContent('banners.supplies.tier3_price', 55)}</span>
                  <span className="bg-terracotta rounded-full px-3 py-1">5 for ${getContent('banners.supplies.tier5_price', 90)}</span>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Hero Sections */}
        <TabsContent value="hero" className="space-y-6">
          {['home', 'teas', 'supplements', 'soap'].map((page) => (
            <div key={page} className="bg-white rounded-2xl shadow-card p-6">
              <h3 className="font-heading text-lg text-text-primary mb-4 capitalize">{page === 'home' ? 'Homepage' : page} Hero</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Overline</Label>
                  <Input value={getContent(`hero.${page}.overline`)} onChange={(e) => updateContent(`hero.${page}.overline`, e.target.value)} className="rounded-xl mt-1" />
                </div>
                <div>
                  <Label>Headline</Label>
                  <Input value={getContent(`hero.${page}.headline`)} onChange={(e) => updateContent(`hero.${page}.headline`, e.target.value)} className="rounded-xl mt-1" />
                </div>
                <div className="md:col-span-2">
                  <Label>Subheadline</Label>
                  <Textarea value={getContent(`hero.${page}.subheadline`)} onChange={(e) => updateContent(`hero.${page}.subheadline`, e.target.value)} className="rounded-xl mt-1" rows={2} />
                </div>
                <div className="md:col-span-2">
                  <ImageUploader label="Background Image" currentImage={getContent(`hero.${page}.background_image`)} onImageChange={(url) => updateContent(`hero.${page}.background_image`, url)} />
                </div>
              </div>
            </div>
          ))}
        </TabsContent>

        {/* Homepage Features */}
        <TabsContent value="homepage">
          <div className="bg-white rounded-2xl shadow-card p-6 space-y-6">
            <h3 className="font-heading text-xl text-text-primary">Homepage Features</h3>
            
            {getContent('homepage.features', []).map((feature, index) => (
              <div key={index} className="border rounded-xl p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Title</Label>
                    <Input 
                      value={feature.title || ''} 
                      onChange={(e) => {
                        const features = [...getContent('homepage.features', [])];
                        features[index] = { ...features[index], title: e.target.value };
                        updateContent('homepage.features', features);
                      }} 
                      className="rounded-xl mt-1" 
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Description</Label>
                    <Input 
                      value={feature.description || ''} 
                      onChange={(e) => {
                        const features = [...getContent('homepage.features', [])];
                        features[index] = { ...features[index], description: e.target.value };
                        updateContent('homepage.features', features);
                      }} 
                      className="rounded-xl mt-1" 
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Banners */}
        <TabsContent value="banners" className="space-y-6">
          {['teas', 'supplements', 'soap', 'supplies'].map((category) => (
            <div key={category} className="bg-white rounded-2xl shadow-card p-6">
              <h3 className="font-heading text-lg text-text-primary mb-4 capitalize">{category} Banner</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Banner Text</Label>
                  <Input value={getContent(`banners.${category}.text`)} onChange={(e) => updateContent(`banners.${category}.text`, e.target.value)} className="rounded-xl mt-1" />
                </div>
                <div>
                  <Label>Savings Text</Label>
                  <Input value={getContent(`banners.${category}.savings`)} onChange={(e) => updateContent(`banners.${category}.savings`, e.target.value)} className="rounded-xl mt-1" />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={getContent(`banners.${category}.enabled`, false)} onChange={(e) => updateContent(`banners.${category}.enabled`, e.target.checked)} className="w-5 h-5 rounded" />
                  <Label>Enable Banner</Label>
                </div>
              </div>
            </div>
          ))}
        </TabsContent>

        {/* Footer */}
        <TabsContent value="footer">
          <div className="bg-white rounded-2xl shadow-card p-6 space-y-4">
            <h3 className="font-heading text-xl text-text-primary mb-4">Footer Content</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Footer Tagline</Label>
                <Input value={getContent('footer.tagline')} onChange={(e) => updateContent('footer.tagline', e.target.value)} className="rounded-xl mt-1" />
              </div>
              <div className="md:col-span-2">
                <Label>About Text</Label>
                <Textarea value={getContent('footer.about')} onChange={(e) => updateContent('footer.about', e.target.value)} className="rounded-xl mt-1" rows={3} />
              </div>
              <div>
                <Label>Contact Email</Label>
                <Input value={getContent('footer.contact_email')} onChange={(e) => updateContent('footer.contact_email', e.target.value)} className="rounded-xl mt-1" />
              </div>
              <div>
                <Label>Contact Phone</Label>
                <Input value={getContent('footer.contact_phone')} onChange={(e) => updateContent('footer.contact_phone', e.target.value)} className="rounded-xl mt-1" />
              </div>
              <div>
                <Label>Instagram URL</Label>
                <Input value={getContent('footer.social_instagram')} onChange={(e) => updateContent('footer.social_instagram', e.target.value)} className="rounded-xl mt-1" placeholder="https://instagram.com/..." />
              </div>
              <div>
                <Label>Facebook URL</Label>
                <Input value={getContent('footer.social_facebook')} onChange={(e) => updateContent('footer.social_facebook', e.target.value)} className="rounded-xl mt-1" placeholder="https://facebook.com/..." />
              </div>
              <div>
                <Label>TikTok URL</Label>
                <Input value={getContent('footer.social_tiktok')} onChange={(e) => updateContent('footer.social_tiktok', e.target.value)} className="rounded-xl mt-1" placeholder="https://tiktok.com/..." />
              </div>
              <div>
                <Label>Copyright Text</Label>
                <Input value={getContent('footer.copyright')} onChange={(e) => updateContent('footer.copyright', e.target.value)} className="rounded-xl mt-1" />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Policies */}
        <TabsContent value="policies">
          <div className="bg-white rounded-2xl shadow-card p-6 space-y-4">
            <h3 className="font-heading text-xl text-text-primary mb-4">Policies & Info Pages</h3>
            <div>
              <Label>Shipping Policy</Label>
              <Textarea value={getContent('policies.shipping')} onChange={(e) => updateContent('policies.shipping', e.target.value)} className="rounded-xl mt-1" rows={4} />
            </div>
            <div>
              <Label>Returns Policy</Label>
              <Textarea value={getContent('policies.returns')} onChange={(e) => updateContent('policies.returns', e.target.value)} className="rounded-xl mt-1" rows={4} />
            </div>
            <div>
              <Label>About Us</Label>
              <Textarea value={getContent('policies.about')} onChange={(e) => updateContent('policies.about', e.target.value)} className="rounded-xl mt-1" rows={5} />
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ContentTab;
